import { useState, useEffect } from "react";
import useTitanApp from "../../../hooks/useTitanApp";
import "../../../assets/css/PatientConsultation.scss";
import {
  setPatientSummary,
  setPatientAIChatMessages,
  setIsPatientDashboard,
  openPatientForm,
  setShowPlayPauseBtn,
  setConsultationPaused,
  setIsCallStarted,
  setPreConsultationTemplate,
  getPatientsListPowerSearch,
  importPatientFromTitan,
  setFilteredPatients,
} from "../../../dispatchers/patientDispatchers";
import {
  setIsTemplateSelected,
  setSelectedTemplate,
  setSelectedTemplateContent,
} from "../../../dispatchers/templateDispatchers";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  setisPatientSearched,
  setPatientInfoTab,
  setCurrentGlobalTabValue,
  setCurrentGlobalTabText,
  setShowPermissionMessage,
  setShowPermissionModal,
} from "../../../dispatchers/appDispatchers";
import { Search, Plus, Download, Database, CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import MiniCalendarControl from "./MiniCalendar/MiniCalendarControl";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@mui/material";
import { getFormattedDate } from "../../../utils/FormattedDate";
import {
  setShowBookingSlots,
  setBookingSlotsMode,
  setShowBacktoSlotsButton,
} from "../../../dispatchers/scheduleEventsDispatchers";
import { checkUserPermissions } from "../../../utils/checkUserPermissions";
import { accessTitanversePatientUpsert } from "../../../data/constantStrings";
import { accessTitanverseCalenderUpsert } from "../../../data/constantStrings";
import { accessTitanverseCalenderView } from "../../../data/constantStrings";

/**
 * Calculate search relevance score for a patient
 * Higher score = better match
 */
const calculateRelevanceScore = (patient, searchQuery) => {
  const query = searchQuery.toLowerCase().trim();
  let score = 0;

  // Extract searchable fields
  const name = (patient.patientName || "").toLowerCase();
  const nhs = (patient.nhsNumber || "").toLowerCase();
  const address = (patient.address || "").toLowerCase();
  const dob = (patient.dob || "").toLowerCase();

  // 1. Exact match (highest priority) - 1000 points
  if (name === query) score += 1000;
  if (nhs === query) score += 1000;

  // 2. Starts with query - 500 points
  if (name.startsWith(query)) score += 500;
  if (nhs.startsWith(query)) score += 500;
  if (address.startsWith(query)) score += 300;

  // 3. Word boundary match (word starts with query) - 400 points
  const nameWords = name.split(/\s+/);
  const addressWords = address.split(/\s+/);

  nameWords.forEach((word) => {
    if (word.startsWith(query)) score += 400;
  });

  addressWords.forEach((word) => {
    if (word.startsWith(query)) score += 200;
  });

  // 4. Contains query - 200 points
  if (name.includes(query)) score += 200;
  if (nhs.includes(query)) score += 200;
  if (address.includes(query)) score += 100;

  // 5. Position bonus (earlier in string = higher score)
  const nameIndex = name.indexOf(query);
  if (nameIndex !== -1) {
    score += Math.max(0, 100 - nameIndex * 2); // Earlier positions get higher bonus
  }

  const nhsIndex = nhs.indexOf(query);
  if (nhsIndex !== -1) {
    score += Math.max(0, 100 - nhsIndex * 2);
  }

  // 6. Length similarity bonus (closer to query length = better)
  if (name.length > 0) {
    const lengthDiff = Math.abs(name.length - query.length);
    score += Math.max(0, 50 - lengthDiff * 2);
  }

  // 7. Consecutive character match bonus
  let consecutiveMatches = 0;
  let maxConsecutive = 0;
  for (let i = 0; i < name.length - query.length + 1; i++) {
    if (name.substring(i, i + query.length) === query) {
      consecutiveMatches = query.length;
      maxConsecutive = Math.max(maxConsecutive, consecutiveMatches);
      score += 300; // Bonus for consecutive match
    }
  }

  return score;
};

/**
 * Sort patients by relevance to search query
 */
const sortPatientsByRelevance = (patients, searchQuery) => {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return patients;
  }

  return [...patients].sort((a, b) => {
    const scoreA = calculateRelevanceScore(a, searchQuery);
    const scoreB = calculateRelevanceScore(b, searchQuery);

    // Higher score first
    return scoreB - scoreA;
  });
};

export default function GlobalSearch() {
  const { appState, patientState } = useTitanApp();
  const { features, currentPharmacyId, darkMode, companyId } = appState || {};
  const { ifKanbanSearchBoxEnabled, ifPharmacyDashboardEnabled } =
    features || {};
  const {
    filteredPatients,
    importPatientFromTitanLoader,
    filteredPatientsLoader,
  } = patientState || {};
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [includeTopSearchTitanPatients, setIncludeTopSearchTitanPatients] =
    useState(false);
  const [cachedPatients, setCachedPatients] = useState([]);
  const [lastApiKeyword, setLastApiKeyword] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const navigateFromDashboard =
    location.state && location?.state?.moveFromDashboard;
  const selectedPatientName =
    location.state && location?.state?.selectedPatientName;
  const selectedPatientAddress =
    location.state && location?.state?.selectedPatientAddress;
  const pharmacyAvgVal = location.state && location?.state?.pharmacyAvgVal;

  // Sorted patients based on search relevance
  const sortedPatients = sortPatientsByRelevance(
    filteredPatients || [],
    searchQuery,
  );
  const canAddPatient = checkUserPermissions(accessTitanversePatientUpsert);
  useEffect(() => {
    if (filteredPatients && filteredPatients.length > 0) {
      setCachedPatients(filteredPatients);
    }
  }, [filteredPatients]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    const normalizedQuery = query.trim().toLowerCase();

    setSearchQuery(query);

    if (normalizedQuery.length === 0) {
      setShowSearchResults(false);
      setCachedPatients([]);
      setLastApiKeyword("");
      setFilteredPatients([]);
      return;
    }

    setShowSearchResults(true);

    if (normalizedQuery.length < 2 || !currentPharmacyId) {
      return;
    }

    const shouldHitApi =
      !lastApiKeyword || !normalizedQuery.startsWith(lastApiKeyword);

    if (shouldHitApi) {
      const payload = {
        Keyword: normalizedQuery,
        Practitioners: "",
        TreatmentPlan: "",
        IsTitanSearch: false,
        ConsultationStatus: 0,
        PharmacyID: currentPharmacyId,
        companyId: companyId,
      };

      getPatientsListPowerSearch(payload);
      setLastApiKeyword(normalizedQuery);
      return;
    }

    const locallyFiltered = cachedPatients.filter((p) =>
      [p.patientName, p.nhsNumber, p.address]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(normalizedQuery)),
    );

    setFilteredPatients(locallyFiltered);
  };

  useEffect(() => {
    setCachedPatients([]);
    setLastApiKeyword("");
    setFilteredPatients([]);
  }, [currentPharmacyId]);

  const handleSearchFocus = () => {
    if (searchQuery.length > 0) {
      setShowSearchResults(true);
    }
  };

  const handleSearchBlur = () => {
    setTimeout(() => setShowSearchResults(false), 200);
  };

  const handlePatientImport = async (patient, e) => {
    e.stopPropagation();
    setShowSearchResults(true);

    if (!patient) return;

    try {
      const payload = {
        PatientID: patient.patientID || "",
        PharmacyID: currentPharmacyId || "",
      };

      const importedPatient = await importPatientFromTitan(payload);

      if (importedPatient) {
        setSearchQuery("");
        setShowSearchResults(false);
        handlePatientSelect(importedPatient);
      }
    } catch (error) {
      console.error("Error importing patient:", error);
    }
  };

  const handlePatientSelect = (patient) => {
    if (patient.imported === false) {
      return;
    }

    localStorage.setItem("patientDashboardTab", "patient-overview-wrapper");
    setisPatientSearched(true);
    setPatientInfoTab(0);
    setCurrentGlobalTabValue(ifPharmacyDashboardEnabled ? 4 : 3);
    setCurrentGlobalTabText("Patients");
    setIsPatientDashboard(true);
    setIsTemplateSelected(false);
    setSelectedTemplate([]);
    setPatientSummary("");
    setSelectedTemplateContent([]);
    setPatientAIChatMessages([]);
    setConsultationPaused(false);
    setShowPlayPauseBtn(false);
    setIsCallStarted(false);
    setPreConsultationTemplate(null);

    navigate(`patientdashboard-v2/${currentPharmacyId}/${patient?.patientID}`, {
      replace: true,
      state: {
        onDarkModeChange: darkMode,
        moveFromDashboard: navigateFromDashboard,
        selectedPatientName: selectedPatientName,
        selectedPatientAddress: selectedPatientAddress,
        pharmacyAvgVal: pharmacyAvgVal,
        latitude: 54.7023545,
        logitude: -3.2765753,
      },
    });
  };

  const handleSearchTitan = async () => {
    const formattedSearchQuery = /^\d[\d\s]*$/.test(searchQuery.trim())
    ? searchQuery.trim().replace(/\s+/g, "")
    : searchQuery;

    const payload = {
      Keyword: formattedSearchQuery,
      Practitioners: "",
      TreatmentPlan: "",
      IsTitanSearch: true,
      ConsultationStatus: 0,
      PharmacyID: currentPharmacyId,
      companyId: companyId,
    };

    await getPatientsListPowerSearch(payload);
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setIncludeTopSearchTitanPatients(false);
    }
  }, [searchQuery]);

  return (
    <div
      className={`global-search ${darkMode ? "dark-mode" : ""}`}
      style={{ maxWidth: "400px", margin: "0 auto" }}
    >
      {ifKanbanSearchBoxEnabled && (
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            {/* Search Icon */}
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                darkMode ? "text-gray-400" : "text-gray-400"
              }`}
            />

            {/* Search Input */}
            <Input
              placeholder="Search patients..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              maxLength={200}
              className={`pl-10 transition-colors duration-300 ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : "bg-gray-50 border-gray-200"
              }`}
            />

            <button
              onClick={() => {
                if (!canAddPatient) {
                  setShowPermissionMessage("Create or update patient");
                  setShowPermissionModal(true);
                  return;
                }

                setShowSearchResults(false);
                setSearchQuery("");
                openPatientForm(true);
              }}
              className={`absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded transition-colors text-white ${
                canAddPatient
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed opacity-50"
              } ${
                darkMode
                  ? "bg-gray-600 hover:bg-gray-500"
                  : "bg-gray-900 hover:bg-gray-800"
              }`}
              size="sm"
            >
              <Plus className="w-3 h-3" />
              Add Patient
            </button>

            {/* Search Results Dropdown */}
            {showSearchResults && sortedPatients.length > 0 && (
              <div
                className={`global-search-box absolute top-full left-0 right-0 mt-1 border rounded-lg text-sm shadow-lg z-50 max-h-80 overflow-y-auto transition-colors duration-300 ${
                  darkMode
                    ? "bg-gray-800 border-gray-600"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Titan status indicator */}
                {includeTopSearchTitanPatients && (
                  <div
                    className={`p-2 border-b text-center ${
                      darkMode
                        ? "border-gray-700 bg-gray-750"
                        : "border-gray-100 bg-gray-50"
                    }`}
                  >
                    <p
                      className={`text-xs ${
                        darkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      💡 Showing results from Titan database
                    </p>
                  </div>
                )}

                {/* Patient List - Now using sortedPatients instead of filteredPatients */}
                {sortedPatients?.map((patient) => (
                  <div
                    key={patient.id}
                    onClick={() =>
                      patient.imported !== false && handlePatientSelect(patient)
                    }
                    className={`p-3 border-b last:border-b-0 hover:transition-colors duration-200 ${
                      patient.imported === false
                        ? "cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      darkMode
                        ? "border-gray-700 hover:bg-gray-700"
                        : "border-gray-100 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {importPatientFromTitanLoader ||
                        filteredPatientsLoader ? (
                          <Skeleton height={16} width="60%" animation="wave" />
                        ) : (
                          <div
                            className={`text-sm font-medium transition-colors duration-300 ${
                              darkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {patient.patientName}
                          </div>
                        )}

                        {importPatientFromTitanLoader ||
                        filteredPatientsLoader ? (
                          <Skeleton height={14} width="80%" animation="wave" />
                        ) : (
                          <div
                            className={`text-xs transition-colors duration-300 ${
                              darkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            NHS: {patient.nhsNumber} • DOB:{" "}
                            {getFormattedDate(patient.dob)}
                          </div>
                        )}
                        {importPatientFromTitanLoader ||
                        filteredPatientsLoader ? (
                          <Skeleton height={12} width="40%" animation="wave" />
                        ) : (
                          <div
                            className={`text-xs transition-colors duration-300 ${
                              darkMode ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            {patient.address}
                          </div>
                        )}
                      </div>
                      {(patient.imported === false ||
                        patient.imported !== null) &&
                        (importPatientFromTitanLoader ||
                        filteredPatientsLoader ? (
                          <div className="shrink-0">
                            <Skeleton
                              variant="rectangular"
                              height={32}
                              width={84}
                              animation="wave"
                            />
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                              if (!canAddPatient) {
                                setShowPermissionMessage(
                                  "Create or update patient",
                                );
                                setShowPermissionModal(true);
                                return;
                              }

                              handlePatientImport(patient, e);
                            }}
                            className={`shrink-0 transition-colors duration-300 ${
                              canAddPatient
                                ? darkMode
                                  ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                  : "bg-white border-gray-300 hover:bg-gray-50"
                                : darkMode
                                  ? "bg-gray-600 border-gray-600 text-gray-300 cursor-not-allowed opacity-50"
                                  : "bg-gray-200 border-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                            }`}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Import
                          </Button>
                        ))}
                    </div>
                  </div>
                ))}

                {/* Expand to Titan button */}
                {searchQuery.trim() !== "" &&
                  !includeTopSearchTitanPatients && (
                    <div
                      className={`p-3 border-t text-center ${
                        darkMode ? "border-gray-700" : "border-gray-100"
                      }`}
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setIncludeTopSearchTitanPatients(true);
                          setShowSearchResults(true);

                          handleSearchTitan();
                        }}
                        className={`w-full ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                            : "bg-white border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <Database className="w-4 h-4 mr-2" />
                        Expand to Titan
                      </Button>
                      <p
                        className={`text-xs mt-1 ${
                          darkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      >
                        Search Titan database for more results
                      </p>
                    </div>
                  )}
              </div>
            )}

            {/* No Results */}
            {showSearchResults &&
              searchQuery.length > 0 &&
              filteredPatients.length === 0 && (
                <div
                  className={`absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-50 p-4 text-center transition-colors duration-300 ${
                    darkMode
                      ? "bg-gray-800 border-gray-600 text-gray-400"
                      : "bg-white border-gray-200 text-gray-500"
                  }`}
                >
                  <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No patients found</p>
                  <p className="mt-1 text-xs opacity-75">
                    Try searching by name, NHS number, or email
                  </p>
                  <div className="pt-3 mt-3 border-t border-gray-200">
                    <div className="flex justify-center gap-2">
                      <Button
                        onClick={() => {
                          if (!canAddPatient) {
                            setShowPermissionMessage(
                              "Create or update patient",
                            );
                            setShowPermissionModal(true);
                            return;
                          }

                          setShowSearchResults(false);
                          setSearchQuery("");
                          openPatientForm(true);
                        }}
                        className={`text-sm text-white ${
                          canAddPatient
                            ? darkMode
                              ? "bg-blue-600 hover:bg-blue-500"
                              : "bg-blue-600 hover:bg-blue-700"
                            : darkMode
                              ? "bg-gray-600 hover:bg-gray-500"
                              : "bg-gray-400 cursor-not-allowed opacity-50  hover:bg-gray-800"
                        }`}
                        size="sm"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add New Patient
                      </Button>

                      <Button
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          if (!canAddPatient) {
                            setShowPermissionMessage(
                              "Create or update patient",
                            );
                            setShowPermissionModal(true);
                            return;
                          }

                          setIncludeTopSearchTitanPatients(true);
                          setShowSearchResults(true);
                          handleSearchTitan();
                        }}
                        variant="outline"
                        size="sm"
                        className={`${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                            : "bg-white border-gray-300 hover:bg-gray-50"
                        } ${
                          !canAddPatient ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        <Database className="w-4 h-4 mr-2" />
                        Search Titan
                      </Button>
                    </div>
                    <p className="mt-2 text-xs opacity-75">
                      Can't find the patient? Add them to the system
                      {!includeTopSearchTitanPatients &&
                        " or search Titan database"}
                    </p>
                  </div>
                </div>
              )}
          </div>
          {checkUserPermissions(accessTitanverseCalenderUpsert) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setBookingSlotsMode("");
                setShowBacktoSlotsButton(true);
                setShowBookingSlots(true);
              }}
              className={`h-10 w-10 flex items-center justify-center transition-colors duration-300 ${
                darkMode
                  ? "text-gray-300 hover:text-white hover:bg-gray-700"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              }`}
            >
              <CalendarIcon className="w-5 h-5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
